import React from 'react';
import {
  allTimezones,
  useTimezoneSelect,
  type ITimezone,
} from 'react-timezone-select';
export type { ITimezone }; // Add this line to export the type

import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// ... rest of TimeZoneSelector component code
interface TimeZoneSelectorProps {
  selectedTimezone: ITimezone;
  onChange: (timezone: ITimezone) => void;
  label?: string;
}

const TimeZoneSelector: React.FC<TimeZoneSelectorProps> = ({
  selectedTimezone,
  onChange,
  label = 'Please make a selection',
}) => {
  const { options, parseTimezone } = useTimezoneSelect({
    labelStyle: 'abbrev',
    timezones: allTimezones,
  });

  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-[350px]"
          >
            <p className="w-[300px] truncate">
              {parseTimezone(selectedTimezone)?.value
                ? options.find(
                    option =>
                      option.value === parseTimezone(selectedTimezone)?.value,
                  )?.label
                : 'Select timezone'}
            </p>

            <ChevronsUpDown className="w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-2000 w-[350px] p-0">
          <Command>
            <CommandInput placeholder="Search timezone" className="h-9" />
            <CommandList>
              <CommandEmpty>No timezone found</CommandEmpty>
              <CommandGroup>
                {options.map(option => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={currentValue => {
                      onChange(parseTimezone(currentValue));
                      setOpen(false);
                    }}
                  >
                    {option.label}
                    <Check
                      className={cn(
                        'ml-auto',
                        parseTimezone(selectedTimezone)?.value === option.value
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default TimeZoneSelector;
